import React, { createContext, useContext, useState, useEffect } from 'react';

const ShopContext = createContext();

export const useShopContext = () => useContext(ShopContext);

export const ShopProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [deliveryCost, setDeliveryCost] = useState(0);
  const [shippingAddress, setShippingAddress] = useState(null);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    
    const savedAddress = localStorage.getItem('shippingAddress');
    if (savedAddress) {
      setShippingAddress(JSON.parse(savedAddress));
      calculateDeliveryCost(JSON.parse(savedAddress));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
    
    // Calculate total items and price
    let items = 0;
    let price = 0;
    
    cart.forEach(item => {
      items += item.quantity;
      // Use finalPrice if present, otherwise fallback to discountPrice or price
      if (typeof item.finalPrice === 'number') {
        price += item.finalPrice * item.quantity;
      } else {
        price += (item.discountPrice || item.price) * item.quantity;
      }
    });
    
    setTotalItems(items);
    setTotalPrice(price);
  }, [cart]);

  // Calculate delivery cost based on location
  const calculateDeliveryCost = (address) => {
    if (!address) return 0;
    
    // Free delivery in Ventspils
    if (address.city.toLowerCase() === 'ventspils') {
      setDeliveryCost(0);
      return 0;
    }
    
    // Check if outside Latvia
    const outsideLatvia = !['latvia', 'latvija', 'LV', 'lv'].includes(address.country?.toLowerCase());
    
    // Set delivery cost based on location
    const cost = outsideLatvia ? 9.99 : 4.99;
    setDeliveryCost(cost);
    return cost;
  };
  
  // Update shipping address
  const updateShippingAddress = (address) => {
    setShippingAddress(address);
    localStorage.setItem('shippingAddress', JSON.stringify(address));
    calculateDeliveryCost(address);
  };

  // Add item to cart
  const addToCart = (product, quantity = 1, selectedOption = null) => {
    setCart(prevCart => {
      // Create a unique identifier for the product+option combination
      const productId = selectedOption ? `${product.id}-${selectedOption}` : product.id;
      
      const existingItemIndex = prevCart.findIndex(item => {
        // Match by ID and selected option
        return (item.id === product.id) && 
               (item.selectedOption === selectedOption);
      });
      
      if (existingItemIndex >= 0) {
        // Item exists, update quantity
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex].quantity += quantity;
        return updatedCart;
      } else {
        // Item doesn't exist, add new item
        return [...prevCart, { 
          ...product, 
          quantity,
          selectedOption
        }];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (productId, selectedOption = null) => {
    setCart(prevCart => prevCart.filter(item => 
      !(item.id === productId && item.selectedOption === selectedOption)
    ));
  };

  // Update item quantity
  const updateQuantity = (productId, quantity, selectedOption = null) => {
    if (quantity <= 0) {
      removeFromCart(productId, selectedOption);
      return;
    }
    
    setCart(prevCart => 
      prevCart.map(item => 
        (item.id === productId && item.selectedOption === selectedOption)
          ? { ...item, quantity } 
          : item
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
    setDeliveryCost(0);
    localStorage.removeItem('shippingAddress');
    setShippingAddress(null);
  };

  // Get final total including delivery
  const getFinalTotal = () => {
    return totalPrice + deliveryCost;
  };

  const value = {
    cart,
    totalItems,
    totalPrice,
    deliveryCost,
    shippingAddress,
    getFinalTotal,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateShippingAddress,
    clearCart
  };

  return (
    <ShopContext.Provider value={value}>
      {children}
    </ShopContext.Provider>
  );
};

export default ShopContext; 